/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";

export const ChoicesApiParams = z.object({
  page: z
    .number()
    .int()
    .describe("Optional: Page number for paginated results (default is 0)."),
  zap_id: z
    .string()
    .describe("The unique identifier for the Zap containing the step."),
  step_id: z
    .string()
    .describe("The ID of the specific step to get field choices for."),
  account_id: z
    .string()
    .describe(
      "The account ID owning the Zap (from additionalData.canvas.account_id).",
    ),
  selected_api: z
    .string()
    .describe(
      "REQUIRED: The API identifier for the app (e.g., 'GoogleCalendarCLIAPI@1.5.0'). Obtain from get_zap if unsure.",
    ),
  input_field_id: z
    .string()
    .describe(
      "REQUIRED: The ID of the field for which choices are being fetched.",
    ),
  prefill: z
    .string()
    .describe(
      "REQUIRED: The prefill string for the field (e.g., 'calendar.id.summary'). This is usually obtained from the needs_api tool response.",
    ),
  authentication_id: z
    .number()
    .int()
    .optional()
    .describe(
      "Optional: The authentication ID for the connected account. If authentication is set already on Zap, use that. Otherwise, call authentication_check to get the authentication ID for the selected_api.",
    ),
  action_id: z
    .string()
    .optional()
    .describe(
      "Optional: The specific action ID within the app. Obtain from get_zap if unsure.",
    ),
  choices_body_params: z
    .record(z.string())
    .optional()
    .describe(
      "Optional: Additional key-value parameters to include in the body of the request to the downstream choices API.",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Getting field options...' or 'Loading choices...'",
    ),
});

export type ChoicesApiParamsType = z.infer<typeof ChoicesApiParams>;

export const choicesApiExecute = async (rawArgs: any) => {
  let params: ChoicesApiParamsType;
  try {
    params = ChoicesApiParams.parse(rawArgs || {});
  } catch (error) {
    console.error("Error parsing arguments for choicesApi:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for choicesApi. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const {
    zap_id,
    step_id,
    account_id,
    // Rest of the params go into the POST body to the proxy
    ...bodyParamsForProxy
  } = params;

  const queryForProxy = new URLSearchParams({
    zap_id,
    step_id,
    account_id,
  });
  const proxyApiUrl = `/api/canvasui/composer/choicesApi?${queryForProxy.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(bodyParamsForProxy),
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for choicesApi (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for choicesApi (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for choicesApi (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch {
        errorDetailString = `Proxy error for choicesApi (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[CHOICES_API_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[CHOICES_API_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[CHOICES_API_TOOL] Error in choicesApiExecute:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error fetching choices: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const choicesApiDescription = `Get available field choices for a specific field in a Zap step. 
IMPORTANT: This tool often requires details about the step (like 'selected_api', 'action_id') and critical field identifiers ('input_field_id', 'prefill'). If these are not known, use 'get_zap' to get the full Zap structure and 'needs_api' to get the 'prefill' value for the target field. 
IMPORTANT: If the asset ID is known because it is on the canvas, you can skip this step entirely. Do not call choices_api to confirm. 
IMPORTANT: If you encounter authentication issues or cannot retrieve valid field choices from the API, use the handoff tool to transition the user to the Editor interface for manual completion.
Required parameters: 
- zap_id: The ID of the Zap. 
- step_id: The ID of the step. 
- account_id: The ID of the account owning the Zap (MUST be from 'additionalData.canvas.account_id'). 
- selected_api: The app identifier for the step. 
- input_field_id: The ID of the field to get choices for. 
- prefill: The prefill string for the field (obtained from needs_api). 
Optional parameters: authentication_id, action_id, choices_body_params (for the downstream choices API request body), page.

`;

export const choicesApiAtom = atom<JotaiTool<z.infer<typeof ChoicesApiParams>>>(
  () => ({
    name: "choices_api",
    description: choicesApiDescription,
    parameters: ChoicesApiParams,
    execute: atom(null, async (_get, _set, { args }) => {
      return choicesApiExecute(args);
    }),
  }),
);
choicesApiAtom.debugLabel = "choicesApiAtom";
/* eslint-enable sort-exports/sort-exports */
